import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquarePlus, Bug, Lightbulb, Calendar, Sparkles, MessageCircle, ThumbsUp, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const feedbackCategories = [
  { id: 'bug', label: 'Bug Reports', icon: Bug, color: 'text-red-500', bgColor: 'bg-red-500/10' },
  { id: 'feature_request', label: 'Feature Requests', icon: Lightbulb, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  { id: 'event_suggestion', label: 'Event Suggestions', icon: Calendar, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  { id: 'experience_suggestion', label: 'Experience Suggestions', icon: Sparkles, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  { id: 'general', label: 'General Feedback', icon: MessageCircle, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10' },
];

const statusConfig = {
  new: { label: 'New', icon: AlertCircle, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  reviewing: { label: 'Reviewing', icon: Clock, color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  in_progress: { label: 'In Progress', icon: Loader2, color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  declined: { label: 'Declined', icon: AlertCircle, color: 'text-neutral-400', bgColor: 'bg-neutral-500/10' },
};

export default function FeedbackPage() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upvoting, setUpvoting] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, [selectedType, selectedStatus]);

  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);

      const response = await fetch(`${API_URL}/api/feedback?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch feedback');

      const data = await response.json();
      setFeedbackList(data.feedback || []);
      setTypeCounts(data.type_counts || {});
      setStatusCounts(data.status_counts || {});
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    setUpvoting(feedbackId);
    try {
      const response = await fetch(`${API_URL}/api/feedback/${feedbackId}/upvote`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to upvote');
      
      // Update local state
      setFeedbackList(prev => 
        prev.map(item => 
          item.id === feedbackId 
            ? { ...item, upvotes: (item.upvotes || 0) + 1 }
            : item
        )
      );
      toast.success('Thanks for your vote!');
    } catch (error) {
      console.error('Error upvoting:', error);
      toast.error('Failed to upvote');
    } finally {
      setUpvoting(null);
    }
  };

  const getCategoryIcon = (type) => {
    const category = feedbackCategories.find(c => c.id === type);
    return category ? category.icon : MessageCircle;
  };

  const getCategoryColor = (type) => {
    const category = feedbackCategories.find(c => c.id === type);
    return category ? category.color : 'text-neutral-400';
  };

  const totalPublic = Object.values(typeCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="feedback-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-neutral-950/90 backdrop-blur-xl border-b border-neutral-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-neutral-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-serif">Community Feedback</h1>
                <p className="text-sm text-neutral-500">See what others are suggesting</p>
              </div>
            </div>
            <Link to="/">
              <span className="font-serif text-lg tracking-wide">
                EVENT<span className="text-amber-400">★</span>GOERS
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl mb-4">
            <MessageSquarePlus className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-3xl font-serif mb-3">Help Us Improve</h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Your feedback shapes the future of EVENT★GOERS. See what the community is suggesting and vote on ideas you love.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {feedbackCategories.map((category) => {
            const Icon = category.icon;
            const count = typeCounts[category.id] || 0;
            const isSelected = selectedType === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedType(isSelected ? null : category.id)}
                className={`p-4 rounded-xl border transition-all duration-200 text-left ${
                  isSelected
                    ? `${category.bgColor} border-2 ${category.color.replace('text-', 'border-')}`
                    : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                }`}
                data-testid={`feedback-filter-${category.id}`}
              >
                <Icon className={`w-5 h-5 mb-2 ${category.color}`} />
                <p className="text-2xl font-semibold">{count}</p>
                <p className="text-xs text-neutral-400">{category.label}</p>
              </button>
            );
          })}
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <span className="text-sm text-neutral-500 mr-2 py-1">Filter by status:</span>
          {Object.entries(statusConfig).map(([key, config]) => {
            const count = statusCounts[key] || 0;
            const isSelected = selectedStatus === key;
            return (
              <button
                key={key}
                onClick={() => setSelectedStatus(isSelected ? null : key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm transition-all ${
                  isSelected
                    ? `${config.bgColor} ${config.color}`
                    : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
                }`}
                data-testid={`feedback-status-filter-${key}`}
              >
                <config.icon className="w-3.5 h-3.5" />
                {config.label}
                <span className="text-xs opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : feedbackList.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquarePlus className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No public feedback yet</h3>
            <p className="text-neutral-500 mb-6">Be the first to share your ideas!</p>
            <p className="text-sm text-neutral-600">
              Use the feedback button in the bottom-right corner to submit your suggestions.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbackList.map((item) => {
              const CategoryIcon = getCategoryIcon(item.type);
              const categoryColor = getCategoryColor(item.type);
              const status = statusConfig[item.status] || statusConfig.new;
              const StatusIcon = status.icon;

              return (
                <div
                  key={item.id}
                  className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-5 hover:border-neutral-700 transition-colors"
                  data-testid={`feedback-item-${item.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Upvote Button */}
                    <button
                      onClick={() => handleUpvote(item.id)}
                      disabled={upvoting === item.id}
                      className="flex flex-col items-center gap-1 px-3 py-2 bg-neutral-800/50 rounded-lg hover:bg-neutral-800 transition-colors group"
                      data-testid={`upvote-button-${item.id}`}
                    >
                      <ThumbsUp className={`w-5 h-5 transition-colors ${upvoting === item.id ? 'text-amber-400' : 'text-neutral-500 group-hover:text-amber-400'}`} />
                      <span className="text-sm font-medium">{item.upvotes || 0}</span>
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <CategoryIcon className={`w-4 h-4 ${categoryColor}`} />
                        <span className={`text-xs font-medium ${categoryColor}`}>
                          {feedbackCategories.find(c => c.id === item.type)?.label || item.type}
                        </span>
                        <Badge variant="outline" className={`${status.bgColor} ${status.color} border-0 text-xs`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-lg mb-2">{item.title}</h3>
                      <p className="text-neutral-400 text-sm line-clamp-3">{item.description}</p>
                      
                      {/* Admin Response */}
                      {item.admin_response && (
                        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <p className="text-xs text-amber-400 font-medium mb-1">Response from EVENT★GOERS:</p>
                          <p className="text-sm text-neutral-300">{item.admin_response}</p>
                        </div>
                      )}

                      {/* Meta */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                        {item.user_name && <span>by {item.user_name}</span>}
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
